/**
 * GWP Code Gate
 *
 * Bridges applied discount codes to a cart attribute so DiscountKit's Gift With
 * Purchase discount can require them. Shopify Functions cannot see which
 * discount codes a customer entered, but they can read cart attributes, so this
 * writes `_gwp_unlocked` for DiscountKit to match on with its cart attribute
 * condition (key `_gwp_unlocked`, value `true`).
 *
 * Verified on ca.pvl.com: with the attribute absent the gift is not added and
 * no discount applies; with it set to "true" DiscountKit auto-adds the gift at
 * $0.00. The gift and the code discount apply together, which the DiscountKit
 * GWP must stay configured to allow ("combine with product discounts").
 *
 * The gate only ever grants what the theme can vouch for. No configured codes
 * means it clears the attribute rather than granting one - a campaign that ends
 * must not leave carts holding a pass to a gift nobody is offering any more.
 *
 * Only the cart page and drawer show the gift, but the code arrives via a
 * /discount/CODE link that can land anywhere, so this runs sitewide.
 */

const GWP_ATTRIBUTE_KEY = "_gwp_unlocked";

/**
 * Split the merchant's comma-separated setting into normalized codes.
 * @param {string} raw
 * @returns {Array<string>}
 */
function parseRequiredCodes(raw) {
  return (raw || "")
    .split(",")
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Codes Shopify is actually honouring. Rejected codes stay on the cart with
 * `applicable: false`, so they must not count towards unlocking.
 * @param {Object} cart
 * @returns {Array<string>}
 */
function appliedCodes(cart) {
  const codes = (cart && cart.discount_codes) || [];
  return codes
    .filter((entry) => entry && entry.applicable)
    .map((entry) => String(entry.code || "").trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Whether this cart has earned the gift.
 * @param {Object} cart
 * @param {Array<string>} requiredCodes
 * @returns {boolean}
 */
function isUnlocked(cart, requiredCodes) {
  if (requiredCodes.length === 0) return false;

  const applied = appliedCodes(cart);
  return requiredCodes.some((code) => applied.includes(code));
}

/**
 * Whether to ask the browser for a fresh render after the gate changed.
 *
 * Discount Kit resolves discount eligibility server-side when the page renders
 * and caches it for the life of that page - neither its own cart-changed event
 * nor notifyCartChanged() re-resolves it, so a gate change cannot reach it
 * without a render. Anywhere but the cart page the customer's next navigation
 * supplies one; on the cart page they would sit and wait, so reload for them.
 *
 * @param {string} pathname
 * @param {string|null} lastReloadValue - value the page last reloaded for
 * @param {string} value - value just written
 * @returns {boolean}
 */
function shouldReloadForGate(pathname, lastReloadValue, value) {
  if (!pathname.includes("/cart")) return false;

  // Reloading for a value already reloaded for would loop, since the reloaded
  // page computes the same value again.
  return lastReloadValue !== value;
}

const RELOAD_MARKER_KEY = "gwpCodeGateReloadedFor";

class GwpCodeGate extends HTMLElement {
  connectedCallback() {
    this.requiredCodes = parseRequiredCodes(this.dataset.requiredDiscountCodes);

    // An unset code list is indistinguishable from a broken gate when you are
    // staring at a cart wondering where the gift went, so say so out loud.
    if (this.requiredCodes.length === 0) {
      console.warn(
        "[GWP Code Gate] No required discount codes configured, so no gift will be released. " +
          "Set them in Theme settings > Cart > Gift with purchase."
      );
    }
    this.isSyncing = false;
    this.sync = this.sync.bind(this);

    this.cartEvents = [
      "cart:updated",
      "cart:update",
      "cart:build",
      "cart:change",
      "cart:refresh",
      "ajaxProduct:added",
      "product:added-to-cart",
      "theme:cart:change",
      "cart-drawer:open",
    ];
    this.cartEvents.forEach((name) => document.addEventListener(name, this.sync));

    // This theme fires cart events inconsistently, so a slow poll backstops
    // them. Paused while the tab is hidden to keep it off the storefront's
    // request budget.
    document.addEventListener("visibilitychange", () => this.setPolling());
    this.setPolling();

    this.sync();
  }

  disconnectedCallback() {
    this.cartEvents.forEach((name) =>
      document.removeEventListener(name, this.sync)
    );
    clearInterval(this.pollInterval);
  }

  setPolling() {
    clearInterval(this.pollInterval);
    if (document.visibilityState === "visible") {
      this.pollInterval = setInterval(this.sync, 15000);
    }
  }

  /**
   * Read the cart and write the attribute if the verdict changed.
   */
  async sync() {
    if (this.isSyncing) return;
    this.isSyncing = true;

    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;

      const cart = await response.json();

      // Writing to an empty cart would create one for every visitor who never
      // shops. An empty cart cannot receive a gift anyway, and a stale
      // attribute on one is corrected by the sync that follows the first item.
      if (!cart.item_count) return;

      const wanted = isUnlocked(cart, this.requiredCodes) ? "true" : "";
      const current = (cart.attributes || {})[GWP_ATTRIBUTE_KEY] || "";

      // Writing is itself a cart change, which re-triggers this sync. Only
      // writing on an actual change is what stops that from looping.
      if (wanted !== current) {
        await this.writeAttribute(wanted);
      }
    } catch (error) {
      console.error("[GWP Code Gate] Cart sync failed:", error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * @param {string} value - "true" to unlock, "" to remove the attribute
   */
  async writeAttribute(value) {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ attributes: { [GWP_ATTRIBUTE_KEY]: value } }),
    });

    if (!response.ok) {
      console.error("[GWP Code Gate] Could not write cart attribute");
      return;
    }

    this.reloadIfCartPage(value);
  }

  /**
   * Discount Kit only learns the gate changed when the page renders again.
   * @param {string} value - value just written
   */
  reloadIfCartPage(value) {
    let lastReloadValue = null;
    try {
      lastReloadValue = sessionStorage.getItem(RELOAD_MARKER_KEY);
    } catch (error) {
      // Storage can be blocked. Without the marker a reload still happens once,
      // because the reloaded page finds the attribute already correct and never
      // writes again.
    }

    if (!shouldReloadForGate(window.location.pathname, lastReloadValue, value)) {
      return;
    }

    try {
      sessionStorage.setItem(RELOAD_MARKER_KEY, value);
    } catch (error) {
      // see above
    }

    window.location.reload();
  }
}

if (!customElements.get("gwp-code-gate")) {
  customElements.define("gwp-code-gate", GwpCodeGate);
}

// Exposed for the self-check in scripts/gwp-code-gate.test.js. Inert in the
// browser, where `module` is undefined.
if (typeof module !== "undefined") {
  module.exports = {
    parseRequiredCodes,
    appliedCodes,
    isUnlocked,
    shouldReloadForGate,
  };
}
